/**
 * QueuedMessageBar — shows pending messages waiting to be sent.
 *
 * Appears above the composer while messages are waiting to be sent. Each item can be
 * edited inline or removed before it is sent. The message already in flight is not
 * listed: it is in the transcript as an optimistic bubble, and listing it again made
 * the bar flash "Waiting for twins..." for the length of every send request.
 */

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Pencil, Check } from '@digital-twins/design-system/icons'
import type { QueueBlockedReason, QueuedMessage } from '../useChatRoom'
import { SLOW_REPLY_NOTICE_MS } from '../queueReplyProgress'

import type { QueueReplyProgress } from '../queueReplyProgress'

interface QueuedMessageBarProps {
  queue: Array<QueuedMessage>
  isSending: boolean
  /** Why the queue cannot drain, from useChatRoom. Null means it can. */
  blockedReason?: QueueBlockedReason | null
  /**
   * Which message the server is answering in this burst. Null when the
   * server has not taken a turn this client handed it. The bar does not
   * invent a position from elapsed time.
   */
  replyProgress?: QueueReplyProgress | null
  /** When that reply became the current one. Null when none is open. */
  replyStartedAt?: number | null
  onEdit: (id: string, content: string) => void
  onRemove: (id: string) => void
  onRetry?: (id: string) => void
  blockedQueuedId?: string | null
}

function useReplyTakingLong(startedAt: number | null, replyKey: string | null): boolean {
  const [isSlow, setIsSlow] = useState(false)

  useEffect(() => {
    if (startedAt == null || replyKey == null) {
      setIsSlow(false)
      return
    }
    const elapsed = Date.now() - startedAt
    if (elapsed >= SLOW_REPLY_NOTICE_MS) {
      setIsSlow(true)
      return
    }
    setIsSlow(false)
    const timer = setTimeout(() => setIsSlow(true), SLOW_REPLY_NOTICE_MS - elapsed)
    return () => clearTimeout(timer)
  }, [startedAt, replyKey])

  return isSlow
}

interface ItemProps {
  item: QueuedMessage
  onEdit: (id: string, content: string) => void
  onRemove: (id: string) => void
}

function QueueItem(props: ItemProps) {
  const { item, onEdit, onRemove } = props
  const { t } = useTranslation('app')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(item.content)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.selectionStart = textareaRef.current.value.length
    }
  }, [editing])

  // Keep draft in sync if the queue item is mutated externally.
  useEffect(() => {
    if (!editing) setDraft(item.content)
  }, [item.content, editing])

  function commitEdit() {
    const trimmed = draft.trim()
    if (!trimmed) {
      setEditing(false)
      return
    }
    // An unchanged conflict must still reach onEdit. That id is already bound to
    // another payload, and confirming the edit is how the client mints a new one.
    const unchanged = trimmed === item.content
    if (!unchanged || item.recoveryReason === 'client_request_conflict') {
      onEdit(item.id, trimmed)
    }
    setEditing(false)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      commitEdit()
    }
    if (e.key === 'Escape') {
      setDraft(item.content)
      setEditing(false)
    }
  }

  return (
    <div
      className="flex items-start gap-2 rounded-xl bg-twin-chat-bubble/25 px-3 py-2 text-sm text-twin-chat-fg-muted transition-colors"
      data-testid={`queued-message-${item.id}`}
    >
      {/* Status indicator */}
      <span className="mt-0.5 shrink-0">
        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-twin-chat-fg-muted/50 text-[9px] font-semibold text-twin-chat-fg-muted">
          {/* queue position dot */}
        </span>
      </span>

      {/* Content / editable area */}
      <div className="min-w-0 flex-1">
        {editing ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={4000}
            onBlur={commitEdit}
            rows={1}
            className="w-full resize-none bg-transparent text-sm text-twin-chat-fg outline-none placeholder:text-twin-chat-fg-muted"
            data-testid={`queued-message-edit-input-${item.id}`}
          />
        ) : (
          <span className="line-clamp-2 break-words leading-snug">{item.content}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        {editing ? (
          <button
            type="button"
            onClick={commitEdit}
            className="rounded p-0.5 text-twin-chat-send hover:bg-twin-chat-send/15 transition-colors"
            aria-label={t('chatChrome.confirmEdit')}
            data-testid={`queued-message-confirm-${item.id}`}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded p-0.5 text-twin-chat-fg-muted hover:text-twin-chat-fg hover:bg-twin-chat-bubble/40 transition-colors"
            aria-label={t('chatChrome.editQueued')}
            data-testid={`queued-message-edit-${item.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          className="rounded p-0.5 text-twin-chat-fg-muted hover:text-destructive hover:bg-destructive/10 transition-colors"
          aria-label={t('chatChrome.removeQueued')}
          data-testid={`queued-message-remove-${item.id}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

export function QueuedMessageBar(props: QueuedMessageBarProps) {
  const {
    queue,
    isSending,
    blockedReason = null,
    replyProgress = null,
    replyStartedAt = null,
    onEdit,
    onRemove,
    blockedQueuedId,
    onRetry,
  } = props
  const { t } = useTranslation('app')

  const queuedLabel = t('chatChrome.queued', { count: queue.length })
  const progressLabel = replyProgress
    ? t('chatChrome.replyingTo', { current: replyProgress.current, total: replyProgress.total })
    : null
  const replyKey = blockedQueuedId || !replyProgress
    ? null
    : `reply:${replyProgress.current}`
  const isReplySlow = useReplyTakingLong(replyStartedAt, replyKey)

  // The first item while a send is in flight is already in the transcript, so only
  // messages still waiting their turn are listed. A blocked item stays: it needs the user.
  const waiting = queue.filter((item, idx) => !(idx === 0 && isSending && item.id !== blockedQueuedId))

  // The final reply leaves the queue when the server accepts it, and the twin
  // is still writing. The position and the slow notice stay up until that
  // turn finishes.
  if (waiting.length === 0 && !replyProgress) return null

  // Say why it is waiting whenever it really is waiting. Keying this off the 409
  // flag alone read "1 Queued" through an entire turn the message could not be
  // sent in. A send that failed outranks waiting: it needs the user, not time.
  // The count, or the server's reply position, stays visible beside that reason
  // so a burst is not a single opaque line.
  const statusLabel = blockedQueuedId
    ? t('chatChrome.needsAttention')
    : blockedReason
      ? t('chatChrome.waitingTwins')
      : progressLabel ?? queuedLabel
  const detailLabel = blockedQueuedId
    ? null
    : progressLabel && blockedReason
      ? progressLabel
      : blockedReason
        ? queuedLabel
        : null

  return (
    <div
      className="mx-4 mb-2 flex flex-col gap-1.5 rounded-2xl border border-twin-chat-divider/30 bg-twin-chat-surface/70 p-2 backdrop-blur-sm"
      data-testid="queued-message-bar"
    >
      <p
        className="px-1 text-[10px] font-medium uppercase tracking-wide text-twin-chat-fg-muted/70"
        data-testid={!blockedQueuedId && !blockedReason && progressLabel ? 'queued-reply-progress' : undefined}
      >
        {statusLabel}
      </p>
      {detailLabel && (
        <p
          className="px-1 text-xs font-medium text-twin-chat-fg"
          data-testid={progressLabel && blockedReason ? 'queued-reply-progress' : 'queued-reply-count'}
          aria-live="polite"
        >
          {detailLabel}
        </p>
      )}
      {isReplySlow && (
        <p
          className="px-1 text-xs text-twin-chat-fg-muted"
          data-testid="queued-reply-slow"
          role="status"
        >
          {t('chatChrome.replyTakingLonger')}
        </p>
      )}
      {blockedQueuedId && onRetry && (
        <button
          type="button"
          onClick={() => onRetry(blockedQueuedId)}
          disabled={isSending}
          data-testid={`queued-message-retry-${blockedQueuedId}`}
          className="self-start rounded-md px-3 py-1.5 text-sm font-medium text-twin-chat-fg hover:bg-twin-chat-bubble/40 disabled:opacity-50"
        >
          {t('chatChrome.retryMessage')}
        </button>
      )}
      {waiting.map((item) => (
        <QueueItem
          key={item.id}
          item={item}
          onEdit={onEdit}
          onRemove={onRemove}
        />
      ))}
    </div>
  )
}
